import type { TelegramUser } from './types.js';

/**
 * Minimal typing of the Telegram WebApp JS SDK surface we use.
 * The full object is injected by Telegram at `window.Telegram.WebApp`.
 */
export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: TelegramUser;
    start_param?: string;
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  ready: () => void;
  expand: () => void;
  close: () => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  enableClosingConfirmation: () => void;
  onEvent: (event: string, handler: () => void) => void;
  offEvent: (event: string, handler: () => void) => void;
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

/** Return the Telegram WebApp SDK object, or null when running outside Telegram. */
export function getTelegramWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

/** True when the app is actually running inside the Telegram client. */
export function isInsideTelegram(): boolean {
  const wa = getTelegramWebApp();
  return Boolean(wa && wa.initData && wa.initData.length > 0);
}

/**
 * Signal readiness and expand to full height. Safe to call outside Telegram
 * (it simply no-ops).
 */
export function initTelegramViewport(): void {
  const wa = getTelegramWebApp();
  if (!wa) return;
  wa.ready();
  wa.expand();
}

/**
 * The Mini App's height in its last STABLE state.
 *
 * Telegram defines `viewportStableHeight` as excluding transient shrinkage —
 * most importantly the software keyboard — whereas `viewportHeight` and
 * `window.innerHeight` both collapse while it is open. This is the correct
 * reference for anything that must not resize when the user starts typing.
 * Returns null outside Telegram (or if the client predates the field).
 */
export function getViewportStableHeight(): number | null {
  const wa = getTelegramWebApp();
  const h = wa?.viewportStableHeight;
  return typeof h === 'number' && h > 0 ? h : null;
}

/** Subscribe to Telegram viewport changes. Returns an unsubscribe function. */
export function onViewportChanged(handler: () => void): () => void {
  const wa = getTelegramWebApp();
  if (!wa?.onEvent) return () => undefined;
  wa.onEvent('viewportChanged', handler);
  return () => wa.offEvent?.('viewportChanged', handler);
}

/**
 * Mini App lifecycle (Bot API 8.0+).
 *
 * `activated` / `deactivated` are TELEGRAM-level events: they fire when the
 * Mini App is minimised and later reopened, NOT when the page merely loses DOM
 * focus. That distinction is the whole point of using them instead of
 * `visibilitychange` / `focus`, which also fire for the software keyboard,
 * notification shades and every incidental blur.
 *
 * Both return an unsubscribe function and no-op on older clients.
 */
export function onMiniAppActivated(handler: () => void): () => void {
  const wa = getTelegramWebApp();
  if (!wa?.onEvent) return () => undefined;
  wa.onEvent('activated', handler);
  return () => wa.offEvent?.('activated', handler);
}

export function onMiniAppDeactivated(handler: () => void): () => void {
  const wa = getTelegramWebApp();
  if (!wa?.onEvent) return () => undefined;
  wa.onEvent('deactivated', handler);
  return () => wa.offEvent?.('deactivated', handler);
}

/**
 * Close the Mini App so the underlying chat becomes visible again.
 *
 * Returns false outside Telegram (dev/browser), so callers can skip the close
 * and simply leave the opened URL in the new tab.
 */
export function closeMiniApp(): boolean {
  const wa = getTelegramWebApp();
  if (!wa || typeof wa.close !== 'function') return false;
  try {
    wa.close();
    return true;
  } catch {
    return false;
  }
}

/** The raw, signed `initData` string to send to the backend for verification. */
export function getInitData(): string {
  return getTelegramWebApp()?.initData ?? '';
}

/** The (unverified) user object, useful for optimistic UI before login. */
export function getUnsafeUser(): TelegramUser | null {
  return getTelegramWebApp()?.initDataUnsafe.user ?? null;
}

/** The Telegram-reported color scheme, defaulting to dark. */
export function getColorScheme(): 'light' | 'dark' {
  return getTelegramWebApp()?.colorScheme ?? 'dark';
}

/** Fire a haptic impact if the client supports it. */
export function hapticImpact(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light'): void {
  getTelegramWebApp()?.HapticFeedback?.impactOccurred(style);
}

/** Fire a selection-changed haptic if supported. */
export function hapticSelection(): void {
  getTelegramWebApp()?.HapticFeedback?.selectionChanged();
}

/**
 * Open a Telegram link (e.g. a channel). Uses the native SDK inside Telegram so
 * it opens in-app, and falls back to a normal new-tab open in the browser.
 */
export function openTelegramLink(url: string): void {
  const wa = getTelegramWebApp();
  if (wa && typeof wa.openTelegramLink === 'function') {
    try {
      wa.openTelegramLink(url);
      return;
    } catch {
      // fall through to browser open
    }
  }
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/** Paint the Telegram header & background to match the app chrome. */
export function applyTelegramChrome(background: string, header: string): void {
  const wa = getTelegramWebApp();
  if (!wa) return;
  try {
    wa.setBackgroundColor(background);
    wa.setHeaderColor(header);
  } catch {
    // Older clients may not support these; ignore.
  }
}
